import { ArrowLeft, ArrowRight } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * PrevNextNav — the two-card bottom-of-article navigation row.
 *
 * Design plan reference,
 * section 22.3 — at the bottom of every article page, two link
 * cards: ← previous topic (left), next topic → (right).
 *
 * Each card carries a small "Previous" / "Next" kicker above the
 * title so the direction is unambiguous even when the icons get
 * stripped (RTL locales, screen-reader navigation, copy/paste).
 *
 * Either side can be omitted — when only `next` is set, the prev
 * slot still occupies its column so the next card stays right-
 * aligned (the user's eye finds it in the same place regardless
 * of whether prev exists). Same applies if only `prev` is set.
 */

export interface PrevNextNavItem {
  title: string
  href: string
}

export interface PrevNextNavProps extends React.HTMLAttributes<HTMLElement> {
  prev?: PrevNextNavItem
  next?: PrevNextNavItem
  /** Override the kicker labels. Useful for non-English locales. */
  prevLabel?: string
  nextLabel?: string
}

const cardClasses = cn(
  'group flex flex-col gap-1 rounded-lg border border-ia-border bg-card p-4',
  'transition-[border-color,transform] duration-150 ease-out motion-reduce:transition-none',
  'hover:border-[var(--color-ia-sidebar-active-border)]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
)

const kickerClasses = cn(
  'font-mono text-[9px] font-semibold uppercase tracking-[1.2px] text-ia-text-secondary',
)

const titleClasses = cn(
  'text-body-md font-medium text-ia-text group-hover:text-ia-green-text',
)

export const PrevNextNav = React.forwardRef<HTMLElement, PrevNextNavProps>(
  ({ prev, next, prevLabel = 'Previous', nextLabel = 'Next', className, ...rest }, ref) => {
    if (!prev && !next) return null

    return (
      <nav
        ref={ref}
        aria-label="Article navigation"
        className={cn('grid grid-cols-2 gap-3', className)}
        {...rest}
      >
        {prev ? (
          <a href={prev.href} className={cn(cardClasses, 'items-start text-left')}>
            <div className={cn(kickerClasses, 'inline-flex items-center gap-1.5')}>
              <ArrowLeft size={12} aria-hidden="true" />
              <span>{prevLabel}</span>
            </div>
            <span className={titleClasses}>{prev.title}</span>
          </a>
        ) : (
          // Empty slot keeps the next card right-aligned.
          <span aria-hidden="true" />
        )}
        {next ? (
          <a href={next.href} className={cn(cardClasses, 'items-end text-right')}>
            <div className={cn(kickerClasses, 'inline-flex items-center gap-1.5')}>
              <span>{nextLabel}</span>
              <ArrowRight size={12} aria-hidden="true" />
            </div>
            <span className={titleClasses}>{next.title}</span>
          </a>
        ) : (
          <span aria-hidden="true" />
        )}
      </nav>
    )
  },
)
PrevNextNav.displayName = 'PrevNextNav'
